import { AppError, type AppErrorCode } from "@shared/app-error";
import { type Accessor, createMemo, createSignal, onCleanup } from "solid-js";

/**
 * §5.2「リソース管理規律」共通基盤: camera / worker のような
 * acquire / release ペアの Provider が共通で抱える 3 つの不変条件
 *
 *   - acquire の冪等性 (既存があれば再利用、 in-flight 並走は 1 つに集約)
 *   - release による in-flight orphan 回避 (epoch token guard)
 *   - 状態遷移は idle → loading → ready / error の 4 ノード
 *
 * を 1 ファイルに集約する。 各 Provider は init / dispose と toAppError を渡すだけで、
 * race / state machine の hand-rolled 実装は持たない。
 *
 * 状態は discriminated union 1 本で表現し、 impossible state を型レベルで排除する。
 * `resource` 派生 accessor は ergonomic 用途で残し、 caller が union を pattern match する
 * 場面では `state()` を直接使う。
 *
 * 引数なし前提: 現存する 2 Provider (camera / worker) はどちらも引数なしで resource を
 * 取得する。 issue #130 で撤去された audio-capture-provider だけが acquire(stream) と
 * 引数を取っていたため、 旧 factory は `Args` 総称 + `argsEqual` を持っていたが、 撤去済の
 * 唯一の利用者と共に不要になったので落とした (YAGNI)。 引数付き取得が再び必要になったら
 * git history (audio-capture 時代の `Args` 版) を参照して復元する。
 *
 * §issue #101 — epoch token (`acquireToken`): 1 Provider 内で「in-flight init に release が
 * 割り込む」 race を守る。 in-flight init の .then で照合し orphan resource を即
 * 捨てる。 setState だけでは resolve 後の上書きで orphan resource が leak する。 release は
 * `handle.release()` (= camera の明示 release) と Provider scope-death の `onCleanup` の 2 経路
 * のみが行い、 どちらも無条件 dispose する (= 後発 acquire に追い越される lease semantics は
 * 持たない。 camera の per-handoff acquire でも release は単一 actor が acquire より先に走る)。
 */
export type AcquireReleaseState<T> =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; resource: T }
  | { kind: "error"; error: AppError };

type AcquireReleaseHandle<T> = {
  state: Accessor<AcquireReleaseState<T>>;
  /** state.kind === "ready" のときの resource、 それ以外は null。 ergonomic 用途。 */
  resource: Accessor<T | null>;
  acquire: () => Promise<T>;
  /**
   * resource を即 dispose し idle に戻す。 Provider scope-death の `onCleanup` と同じ
   * 無条件 teardown。 camera のように Provider scope (app-layout) が消費 page より長生きする
   * ケースで、 消費者 / startSessionFlow が明示 release するために公開する。
   */
  release: () => void;
};

type AcquireReleaseOptions<T> = {
  init: () => Promise<T>;
  dispose: (resource: T) => void;
  /**
   * 既存 resource が再利用可能か。 default: 常に true。 camera は `(s) => s.active` で
   * inactive な MediaStream を捨てて作り直す。 false なら既存を dispose して fresh init。
   */
  canReuse?: (existing: T) => boolean;
  /** 任意の例外を AppError に正規化。 default: Unexpected/fatal。 */
  toAppError?: (err: unknown) => AppError;
  /** release で in-flight を捨てたとき caller に投げる transient AppError code。 */
  abortedCode: AppErrorCode;
  abortedMessage?: string;
};

const defaultToAppError = (err: unknown): AppError =>
  err instanceof AppError
    ? err
    : new AppError("Unexpected", "fatal", err instanceof Error ? err.message : String(err), err);

export function createAcquireRelease<T>(opts: AcquireReleaseOptions<T>): AcquireReleaseHandle<T> {
  const [state, setState] = createSignal<AcquireReleaseState<T>>({ kind: "idle" });
  const resource = createMemo<T | null>(() => {
    const s = state();
    return s.kind === "ready" ? s.resource : null;
  });
  let pending: Promise<T> | null = null;
  // §5.2 race fix (epoch token): release のたびに新 token に交換する。 in-flight init の .then
  // 経路で起動時 token と現 token が一致するか確認し、 不一致なら解決値を即 dispose して捨てる。
  // setState だけでは resolve 後の上書きで orphan resource が leak する。
  let acquireToken: object = {};

  const canReuse = opts.canReuse ?? (() => true);
  const toAppError = opts.toAppError ?? defaultToAppError;
  const abortedMessage = opts.abortedMessage ?? `released before ${opts.abortedCode} resolved`;

  // 無条件 teardown。 §release の 3 ステップ: resource teardown + signal reset + pending = null +
  // epoch token 世代交代。 Provider scope-death の onCleanup と handle.release() (= camera の明示
  // release) の両方がこれを呼ぶ。
  // idle のときは early return する: session-page teardown が camera を無条件 release するため、
  // camera が元々 idle の経路で release が呼ばれることがあるが、 そこで新オブジェクト
  // {kind:"idle"} を setState すると state signal の無駄な通知が走る。
  const disposeNow = (): void => {
    const s = state();
    if (s.kind === "idle") return;
    if (s.kind === "ready") opts.dispose(s.resource);
    setState({ kind: "idle" });
    pending = null;
    acquireToken = {};
  };

  const acquire = (): Promise<T> => {
    const s = state();
    if (s.kind === "ready" && canReuse(s.resource)) {
      return Promise.resolve(s.resource);
    }
    if (pending) return pending;

    if (s.kind === "ready") opts.dispose(s.resource);
    setState({ kind: "loading" });
    acquireToken = {};
    const myToken = acquireToken;

    const promise = opts.init().then(
      (next) => {
        if (myToken !== acquireToken) {
          opts.dispose(next);
          throw new AppError(opts.abortedCode, "transient", abortedMessage);
        }
        pending = null;
        setState({ kind: "ready", resource: next });
        return next;
      },
      (err: unknown) => {
        const appErr = toAppError(err);
        if (myToken !== acquireToken) throw appErr;
        pending = null;
        setState({ kind: "error", error: appErr });
        throw appErr;
      },
    );
    pending = promise;
    return promise;
  };

  onCleanup(disposeNow);

  return { state, resource, acquire, release: disposeNow };
}
